/**
 * Typed client helpers for the worker admin API (`/api/admin/*`).
 *
 * The generic Refine `users`/`roles` resources already route to `/admin/users`
 * and `/admin/roles` via the data-provider. These helpers cover the admin
 * SUB-resources the generic CRUD does not: per-user roles + permission
 * overrides, the role permission-grant set, and the read-only permissions
 * catalog. All shapes are verified against the live staging worker
 * (AUTH-CHAT-WIRING-CONTRACT §2.1) and the worker route schemas in
 * `apps/worker/src/routes/crud/{users,roles,permissions}.ts`.
 *
 * All HTTP goes through the shared {@link api} client (`credentials: include`,
 * error normalization into {@link ApiError}).
 */
import { api } from "@/lib/api";
import type { RoleName } from "@aos/shared";

/** A single permission in the catalog (`GET /admin/permissions`). */
export interface PermissionRow {
  /** `resource:action`, e.g. `decisions:approve`. */
  key: string;
  resource: string;
  action: string;
  description?: string;
}

/** Worker list-response envelope. */
interface ListEnvelope<T> {
  items: T[];
  total: number;
}

/**
 * A persisted per-user permission override row, as returned by the GET
 * endpoints (`permission_key`, not `key`). The PUT endpoint takes the slimmer
 * {@link OverrideInput} (`{ key, effect }`).
 */
export interface PermissionOverrideRow {
  user_id: string;
  permission_key: string;
  effect: "grant" | "deny";
  created_at?: string;
}

/** The override shape accepted by `PUT /admin/users/:id/permissions`. */
export interface OverrideInput {
  key: string;
  effect: "grant" | "deny";
}

/** `GET /admin/users/:id` — the safe user row plus its roles + overrides. */
export interface AdminUserDetail {
  id: string;
  email: string;
  display_name: string;
  status: string;
  department?: string | null;
  created_at: string;
  updated_at: string;
  roles: RoleName[];
  permission_overrides: PermissionOverrideRow[];
}

/** `GET /admin/roles/:name` — the role descriptor (static rules + extra grants). */
export interface AdminRoleDetail {
  name: RoleName;
  /** Matrix rules baked into the role (read-only here). */
  static_rules?: unknown[];
  /** DB-stored extra permission keys (editable, except for `admin`). */
  extra_grants: string[];
}

/** Fetch the full permissions catalog (the 31 keys). */
export async function fetchPermissionsCatalog(
  signal?: AbortSignal,
): Promise<PermissionRow[]> {
  const env = await api.get<ListEnvelope<PermissionRow>>("/admin/permissions", {
    query: { pageSize: 200 },
    signal,
  });
  return env.items;
}

/** Fetch one user with its roles + permission overrides. */
export function fetchAdminUser(
  id: string,
  signal?: AbortSignal,
): Promise<AdminUserDetail> {
  return api.get<AdminUserDetail>(`/admin/users/${id}`, { signal });
}

/** Replace a user's full role set (`PUT /admin/users/:id/roles`). */
export function putUserRoles(
  id: string,
  roles: RoleName[],
): Promise<{ roles: RoleName[] }> {
  return api.put<{ roles: RoleName[] }>(`/admin/users/${id}/roles`, { roles });
}

/** Replace a user's full override set (`PUT /admin/users/:id/permissions`). */
export function putUserPermissions(
  id: string,
  overrides: OverrideInput[],
): Promise<{ overrides: PermissionOverrideRow[] }> {
  return api.put<{ overrides: PermissionOverrideRow[] }>(
    `/admin/users/${id}/permissions`,
    { overrides },
  );
}

/** Fetch a role's descriptor (static rules + extra grants). */
export function fetchAdminRole(
  name: string,
  signal?: AbortSignal,
): Promise<AdminRoleDetail> {
  return api.get<AdminRoleDetail>(`/admin/roles/${name}`, { signal });
}

/** Fetch a role's editable grant keys (`GET /admin/roles/:name/permissions`). */
export async function fetchRoleGrants(
  name: string,
  signal?: AbortSignal,
): Promise<string[]> {
  const res = await api.get<{ keys: string[] }>(
    `/admin/roles/${name}/permissions`,
    { signal },
  );
  return res.keys;
}

/** Replace a role's full grant set (`PUT /admin/roles/:name/permissions`). */
export function putRoleGrants(
  name: string,
  keys: string[],
): Promise<{ keys: string[] }> {
  return api.put<{ keys: string[] }>(`/admin/roles/${name}/permissions`, {
    keys,
  });
}

/**
 * Group a flat catalog by `resource`, sorted by resource then key, for the
 * grouped catalog page and the matrix. Pure helper.
 */
export function groupByResource(
  rows: PermissionRow[],
): { resource: string; permissions: PermissionRow[] }[] {
  const map = new Map<string, PermissionRow[]>();
  for (const row of rows) {
    const list = map.get(row.resource) ?? [];
    list.push(row);
    map.set(row.resource, list);
  }
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([resource, permissions]) => ({
      resource,
      permissions: permissions.sort((a, b) => a.key.localeCompare(b.key)),
    }));
}
